import { useCallback, useMemo, useRef, useState } from "react";

function parseSSEBlock<TData = unknown>(
  block: string,
): SSEMessage<TData> | null {
  const lines = block
    .replaceAll("\r\n", "\n")
    .replaceAll("\r", "\n")
    .split("\n")
    .map((l) => l.trimEnd());

  const msg: SSEMessage<TData> = {};
  const dataLines: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed) {
      continue;
    }

    if (trimmed.startsWith(":")) {
      continue;
    }

    const idx = trimmed.indexOf(":");
    const field = idx === -1 ? trimmed : trimmed.slice(0, idx);
    const value = idx === -1 ? "" : trimmed.slice(idx + 1).trimStart();

    switch (field) {
      case "event":
        msg.event = value;
        break;
      case "data":
        dataLines.push(value);
        break;
      default:
        break;
    }
  }

  if (dataLines.length === 0 && !msg.event && !msg.created_at) {
    return null;
  }

  const raw = dataLines.join("\n");

  if (raw) {
    try {
      msg.data = JSON.parse(raw) as TData;
    } catch {
      msg.data = raw as unknown as TData;
    }
  }

  return msg;
}

export type SSEConnectionStatus =
  | "idle"
  | "connecting"
  | "open"
  | "closed"
  | "error";

export type SSEMessage<TData = unknown> = {
  event?: string;
  created_at?: string;
  data?: TData;
};

type StartParams = {
  url: string;
  headers?: Record<string, string>;
  body?: unknown;
};

export function useSSEStream<TData = unknown>() {
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<SSEConnectionStatus>("idle");
  const [messages, setMessages] = useState<Array<SSEMessage<TData>>>([]);

  const abortRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;

    setStatus((s) => (s === "idle" ? "idle" : "closed"));
  }, []);

  const clear = useCallback(() => setMessages([]), []);

  const start = useCallback(
    async ({ url, headers, body }: StartParams) => {
      stop();
      setError(null);
      setStatus("connecting");

      const abort = new AbortController();

      abortRef.current = abort;

      try {
        const res = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: {
            Accept: "text/event-stream",
            "Content-Type": "application/json",
            ...(headers ?? {}),
          },
          body: JSON.stringify(body),
          signal: abort.signal,
        });

        if (!res.ok) {
          const text = await res.text().catch(() => "");

          throw new Error(
            `HTTP ${res.status} ${res.statusText}${text ? ` - ${text}` : ""}`,
          );
        }

        if (!res.body) {
          throw new Error("Response has no body (stream unsupported).");
        }

        setStatus("open");

        const reader = res.body.getReader();
        const decoder = new TextDecoder();

        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();

          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true }).replaceAll(
            "\r\n",
            "\n",
          );

          while (true) {
            const sep = buffer.indexOf("\n\n");

            if (sep === -1) {
              break;
            }

            const block = buffer.slice(0, sep);

            buffer = buffer.slice(sep + 2);

            const parsed = parseSSEBlock<TData>(block);

            if (parsed) {
              setMessages((prev) => [...prev, parsed]);
            }
          }
        }

        setStatus("closed");
      } catch (e) {
        if (abort.signal.aborted) {
          return;
        }

        setStatus("error");
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (abortRef.current === abort) {
          abortRef.current = null;
        }
      }
    },
    [stop],
  );

  const lastMessage = useMemo(() => messages[messages.length - 1] ?? null, [
    messages,
  ]);

  return { status, error, messages, lastMessage, start, stop, clear };
}
